import React from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { Border, Color, Grid, Type } from '../../app/StyleGuide';
import { FileUploaderProps } from './models';
import { FullWidthButton } from '../Buttons';
import ErrorMessage from '../ErrorMessage';
import ProgressBar from '../ProgressBar';
import { useFileUploader } from './useFileUploader';

type DropZoneProps = {
  isDragActive?: boolean;
  isDragAccept?: boolean;
  isDragReject?: boolean;
};

const getStatusColor = (props: DropZoneProps): string => {
  if (props.isDragAccept) {
    return `${Color.Green._50}`;
  }
  if (props.isDragReject) {
    return `${Color.Orange._50}`;
  }
  if (props.isDragActive) {
    return `${Color.Secondary._50}`;
  }
  return `${Color.Blue._20}`;
};

const DropZone = styled.div<DropZoneProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: ${Grid._3};
  padding: ${Grid._7};
  border-width: 2px;
  border-radius: ${Border.radius};
  border-color: ${props => getStatusColor(props)};
  border-style: dashed;
  background-color: none;
  color: ${Type.Color.dark};
  outline: none;
  cursor: pointer;
  transition: border 0.24s ease-in-out;
  p {
    color: ${Type.Color.dark};
    margin: 0;
  }
`;

const File = styled.div`
  display: flex;
  align-items: center;
  list-style: none;
  margin: ${Grid._4} auto;
  border-radius: ${Border.radius};
  background: ${Color.Secondary._80};
  color: white;
  font-size: ${Type.Scale._3};
  span {
    padding: ${Grid._5};
    flex-grow: 1;
    word-break: break-all;
  }
  i {
    padding-right: ${Grid._5};
    font-size: ${Type.Scale._5};
    cursor: pointer;
  }
`;

const Container = styled.div`
  text-align: center;
`;

export const FileUploader = (props: FileUploaderProps) => {
  const { file, errorMessage, uploadProgress, acceptsData, onDrop, cancelUpload, maxSize, removeFile } =
    useFileUploader(props);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    maxSize,
    multiple: false,
    accept: acceptsData.mimeTypes,
  });

  return (
    <>
      {uploadProgress !== null && (
        <Container>
          {uploadProgress < 100 && (
            <p className="text-color-medium">{uploadProgress}% - Please wait for file to upload</p>
          )}
          {uploadProgress == 100 && <p className="text-color-medium">Upload complete</p>}
          <ProgressBar progress={uploadProgress} className="tl-progress-bar" style={{ margin: '24px auto' }} />
          {uploadProgress < 100 && <FullWidthButton onClick={cancelUpload}>Cancel</FullWidthButton>}
        </Container>
      )}

      {!file && uploadProgress === null && (
        <>
          <DropZone data-qa-hook="fileUploader" {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
            <input {...getInputProps()} />
            {isDragActive ? <p>Drop the file here</p> : <p>Click to select a file or drag and drop a file</p>}
          </DropZone>
          <p data-qa-hook="acceptsMessage" className="text-color-medium" style={{ textAlign: 'center' }}>
            {acceptsData.message}
          </p>
        </>
      )}

      {!!file && (
        <Container>
          <File>
            <span>{file.name}</span>
            <i className="icon ion-ios-trash-outline" onClick={removeFile}></i>
          </File>
        </Container>
      )}

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </>
  );
};
